import { useState, useEffect, useRef } from "react";
import {
  checkIn, checkOut, getAttendanceByDate, getAttendanceByMonth,
  uploadAttendancePhoto, isHolidayDate, getHolidays, requestLeave,
  requestOvertime, deleteOvertime, getOvertimeByMonth,
  submitAttendanceAppeal, getAppealsByPromoter, addAuditLog,
} from "../../firebase/services";
import { useAuth } from "../../hooks/useAuth";
import { format, getDaysInMonth, addMonths, subMonths } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";
import { ATT_STATUS, CAN_APPEAL, DAY_TYPES, OT_KINDS, getAttendanceStatus, isPendingLeave } from "../../firebase/schema";


function requestNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    Notification.requestPermission();
  }
}

function AppealModal({ record, date, promoterName, promoterId, appeal, onClose, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const handleSubmit = async () => {
    if (!reason.trim()) { setMsg("Vui lòng nhập lý do giải trình."); return; }
    setLoading(true);
    try {
      await submitAttendanceAppeal({
        promoterId, promoterName,
        attendanceId: record?.id || null,
        date, reason: reason.trim(),
      });
      onSubmitted();
      onClose();
    } catch (err) {
      setMsg("Lỗi: " + err.message);
    } finally { setLoading(false); }
  };

  if (appeal) {
    const statusLabel = { pending: "Đang chờ duyệt", approved: "Đã duyệt ✓", rejected: "Bị từ chối" };
    const statusBadge = { pending: "badge-amber", approved: "badge-green", rejected: "badge-red" };
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">Giải trình – {date?.slice(8)}/{date?.slice(5,7)}</div>
            <button className="btn btn-sm" onClick={onClose}>✕</button>
          </div>
          <div style={{ marginBottom: 10 }}>
            <span className={`badge ${statusBadge[appeal.status]}`}>{statusLabel[appeal.status]}</span>
          </div>
          <div style={{ background: "#f5f4f1", borderRadius: 8, padding: 12, fontSize: 13, color: "#555", marginBottom: 10 }}>
            {appeal.reason}
          </div>
          {appeal.adminNote && (
            <div className="alert alert-warn">Ghi chú từ admin: {appeal.adminNote}</div>
          )}
          <button className="btn btn-full" onClick={onClose}>Đóng</button>
        </div>
      </div>
    );
  }

  const statusText = !record
    ? "Ngày này không có dữ liệu chấm công. Nếu bạn có lý do hợp lệ, vui lòng giải trình."
    : !record.checkoutTime
    ? `Bạn đã check-in lúc ${record.checkinTime} nhưng chưa check-out.`
    : record.totalHours < 7
    ? `Bạn làm ${record.totalHours}h, thiếu so với 7h quy định.`
    : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Giải trình – {date?.slice(8)}/{date?.slice(5,7)}</div>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        {statusText && <div className="alert alert-warn" style={{ marginBottom: 12 }}>{statusText}</div>}
        <div className="form-group">
          <label className="form-label">Lý do giải trình *</label>
          <textarea
            rows={4}
            placeholder="Mô tả lý do vắng mặt hoặc không đủ công, kèm bằng chứng nếu có..."
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </div>
        {msg && <div className="alert alert-error">{msg}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSubmit} disabled={loading}>
            {loading ? "Đang gửi..." : "Gửi giải trình"}
          </button>
          <button className="btn" onClick={onClose}>Huỷ</button>
        </div>
      </div>
    </div>
  );
}

// Promoter đăng ký nghỉ cho ngày trong tương lai — đơn ở trạng thái chờ quản lý duyệt,
// chưa tính công cho tới khi được duyệt.
// Bấm vào một ngày hôm nay / tương lai: xin nghỉ hoặc đăng ký tăng ca.
// Cả hai đều chờ quản lý duyệt — chưa duyệt thì chưa tính công / chưa tính OT.
function DayRequestModal({ date, promoterId, promoterName, allowLeave, allowOt, ot, standardHours, isHolidayDay, onClose, onSubmitted }) {
  const { t } = useLanguage();
  const [tab, setTab] = useState(allowLeave ? "leave" : "ot");
  const [dayType, setDayType] = useState(DAY_TYPES.ANNUAL_LEAVE);
  // Ngày lễ: mọi giờ làm đều phải đăng ký trước — mặc định cả ca 8 giờ
  const [hours, setHours] = useState(isHolidayDay ? 8 : 1);
  const [otKind, setOtKind] = useState(isHolidayDay ? OT_KINDS.HOLIDAY : OT_KINDS.EXTRA);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const run = async (fn) => {
    setBusy(true); setMsg("");
    try { await fn(); onSubmitted(); onClose(); }
    catch (err) { setMsg("Lỗi: " + err.message); }
    setBusy(false);
  };
  const submitLeave = () => run(() => requestLeave({ promoterId, promoterName, date, dayType, reason: reason.trim() }));
  const submitOt = () => run(() => requestOvertime({ promoterId, promoterName, date, hours: Number(hours), reason: reason.trim(), kind: otKind }));
  const withdrawOt = () => run(() => deleteOvertime(ot.id, promoterName, false));

  const tabBtn = (key, label) => (
    <button type="button" onClick={() => setTab(key)}
      style={{ flex: 1, padding: "8px", border: "none", cursor: "pointer", fontSize: 13,
        borderBottom: tab === key ? "2px solid #C8152A" : "2px solid transparent",
        background: "transparent", fontWeight: tab === key ? 600 : 400, color: tab === key ? "#C8152A" : "#666" }}>
      {label}
    </button>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{date?.slice(8)}/{date?.slice(5, 7)}/{date?.slice(0, 4)}</div>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>

        {ot && (
          <div className="alert alert-info" style={{ fontSize: 13, marginBottom: 12 }}>
            ⏱ {t("ot.registered", { h: ot.hours })} — <b>{ot.status === "approved" ? t("ot.st_approved") : t("ot.st_pending")}</b>
            {ot.status === "pending" && (
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-sm" disabled={busy} onClick={withdrawOt}>{t("ot.withdraw")}</button>
              </div>
            )}
          </div>
        )}

        {(allowLeave && allowOt) && (
          <div style={{ display: "flex", borderBottom: "1px solid #eee", marginBottom: 12 }}>
            {tabBtn("leave", "🌴 " + t("lv.title"))}
            {tabBtn("ot", "⏱ " + t("ot.title"))}
          </div>
        )}

        {tab === "leave" && allowLeave && (
          <div className="form-group">
            <label className="form-label">{t("lv.type")}</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                [DAY_TYPES.ANNUAL_LEAVE, t("att.leave_annual"), ATT_STATUS.annualLeave.color],
                [DAY_TYPES.UNPAID_LEAVE, t("lv.unpaid"), ATT_STATUS.unpaidLeave.color],
              ].map(([val, label, color]) => (
                <button key={val} type="button" onClick={() => setDayType(val)}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 8, cursor: "pointer", fontSize: 13,
                    border: dayType === val ? "2px solid #C8152A" : "1px solid #e5e7eb",
                    background: dayType === val ? color : "#fff",
                    fontWeight: dayType === val ? 600 : 400,
                  }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 5 }}>
              {dayType === DAY_TYPES.ANNUAL_LEAVE ? t("lv.annual_note") : t("lv.unpaid_note")}
            </div>
          </div>
        )}

        {tab === "ot" && allowOt && (
          <div className="form-group">
            <label className="form-label">{t("ot.kind")}</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {(isHolidayDay
                // Ngày lễ: chỉ một loại — toàn bộ giờ làm tính theo mức ngày lễ
                ? [[OT_KINDS.HOLIDAY, t("ot.kind_holiday")]]
                : [[OT_KINDS.EXTRA, t("ot.kind_extra")], [OT_KINDS.WEEKLY_OFF, t("ot.kind_weekly_off")]]
              ).map(([val, label]) => (
                <button key={val} type="button"
                  onClick={() => { setOtKind(val); if (val === OT_KINDS.WEEKLY_OFF) setHours(standardHours); }}
                  style={{
                    flex: 1, padding: "9px 6px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                    border: otKind === val ? "2px solid #C8152A" : "1px solid #e5e7eb",
                    background: otKind === val ? "#FDECEA" : "#fff", fontWeight: otKind === val ? 600 : 400,
                  }}>{label}</button>
              ))}
            </div>
            <label className="form-label">{t("ot.hours")}</label>
            <input type="number" min="0.5" max="12" step="0.5" value={hours} onChange={e => setHours(e.target.value)} />
            <div style={{ fontSize: 11, color: "#888", marginTop: 5 }}>
              {otKind === OT_KINDS.HOLIDAY ? t("ot.note_holiday")
                : otKind === OT_KINDS.WEEKLY_OFF ? t("ot.note_weekly_off") : t("ot.note", { h: standardHours })}
            </div>
          </div>
        )}

        {(allowLeave || allowOt) && (
          <>
            <div className="form-group">
              <label className="form-label">{t("lv.reason")}</label>
              <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                placeholder={tab === "ot" ? t("ot.reason_ph") : t("lv.reason_ph")} />
            </div>
            <div className="alert alert-info" style={{ fontSize: 12, marginBottom: 10 }}>{t("lv.approval_note")}</div>
          </>
        )}
        {msg && <div className="alert alert-error">{msg}</div>}

        <div style={{ display: "flex", gap: 8 }}>
          {(allowLeave || allowOt) && (
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy}
              onClick={tab === "ot" ? submitOt : submitLeave}>
              {busy ? t("common.saving") : tab === "ot" ? t("ot.submit") : t("lv.submit")}
            </button>
          )}
          <button className="btn" onClick={onClose}>{t("common.cancel")}</button>
        </div>
      </div>
    </div>
  );
}

export default function PromoterCheckin() {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const TODAY = format(new Date(), "yyyy-MM-dd");
  const currentMonth = format(new Date(), "yyyy-MM");

  const [todayRecord, setTodayRecord] = useState(null);
  const [clock, setClock] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef();
  // Ảnh đã chọn — giữ trong state để promoter THẤY ngay là máy đã nhận ảnh
  const [photo, setPhoto] = useState(null); // { blob, url, sizeKB }
  const [preparing, setPreparing] = useState(false);

  // History
  const [viewMonth, setViewMonth] = useState(currentMonth);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [appealModal, setAppealModal] = useState(null); // { date, record, appeal }
  const [holidayDates, setHolidayDates] = useState([]);  // ngày lễ để tô tím cả ngày không đi làm
  const [shift, setShift] = useState("full"); // "full" | "half"
  const { cfgFor, rules } = usePayrollConfig();
  const todayWork = cfgFor(currentMonth).work;   // ca làm hôm nay
  const viewWork = cfgFor(viewMonth).work;       // quy tắc cho tháng đang xem trên lịch
  const scheduledHours = shift === "full" ? todayWork.standardHours : todayWork.halfShiftHours;
  const [dayModal, setDayModal] = useState(null);  // { date, allowLeave, allowOt, ot }
  const [otList, setOtList] = useState([]);
  const otMap = Object.fromEntries(otList.map(o => [o.date, o]));
  const loadOt = () => { if (user) getOvertimeByMonth(viewMonth, user.uid).then(setOtList).catch(() => setOtList([])); };

  // Clock
  useEffect(() => {
    const tick = () => setClock(new Date().toTimeString().slice(0, 8));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Today's record
  useEffect(() => {
    if (user) getAttendanceByDate(user.uid, TODAY).then(setTodayRecord);
  }, [user]);

  // History records + đơn tăng ca của tháng đang xem
  useEffect(() => {
    if (user) getAttendanceByMonth(user.uid, viewMonth).then(setHistoryRecords);
    loadOt();
  }, [user, viewMonth]);

  // Ngày lễ — để tô tím cả những ngày lễ không đi làm
  useEffect(() => {
    getHolidays().then(hs => setHolidayDates(hs.map(h => h.date))).catch(() => {});
  }, []);

  // Appeals
  const loadAppeals = () => {
    if (user) getAppealsByPromoter(user.uid).then(setAppeals);
  };
  useEffect(() => { loadAppeals(); }, [user]);

  // 10 PM checkout reminder notification
  useEffect(() => {
    requestNotificationPermission();
    const check = () => {
      if (!todayRecord || todayRecord.checkoutTime) return;
      const h = new Date().getHours();
      const m = new Date().getMinutes();
      if (h === rules.checkoutRemindHour && m < 2) {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("PromoterHQ – Nhắc nhở check-out!", {
            body: `Đã ${rules.checkoutRemindHour}:00 rồi! Bạn chưa check-out hôm nay. Vui lòng check-out ngay nhé.`,
            icon: "/favicon.ico",
            tag: "checkout-reminder",
          });
        }
      }
    };
    const id = setInterval(check, 60000);
    check();
    return () => clearInterval(id);
  }, [todayRecord, rules.checkoutRemindHour]);

  // GPS có timeout 8s — không để treo vô hạn khi máy khó bắt sóng
  const getGPS = () => new Promise((res) => {
    if (!navigator.geolocation) { res({ lat: 0, lng: 0 }); return; }
    const fallback = setTimeout(() => res({ lat: 0, lng: 0 }), 10000);
    navigator.geolocation.getCurrentPosition(
      p => { clearTimeout(fallback); res({ lat: p.coords.latitude, lng: p.coords.longitude }); },
      () => { clearTimeout(fallback); res({ lat: 0, lng: 0 }); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  });

  // Nén ảnh trước khi upload (ảnh camera 3–12MB → ~200-400KB) — tăng tốc rõ rệt trên mạng yếu
  // Có timeout 15s: vài máy Android không bắn onload/onerror với ảnh HEIC → tránh treo vĩnh viễn
  const compressPhoto = (file, maxDim = 1280, quality = 0.7) => new Promise((resolve) => {
    let done = false;
    const finish = (result) => { if (!done) { done = true; resolve(result); } };
    const guard = setTimeout(() => finish(file), 15000);
    const img = new Image();
    const src = URL.createObjectURL(file);
    img.onload = () => {
      clearTimeout(guard);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(b => { URL.revokeObjectURL(src); finish(b || file); }, "image/jpeg", quality);
    };
    img.onerror = () => { clearTimeout(guard); URL.revokeObjectURL(src); finish(file); };
    img.src = src;
  });

  // Chọn/chụp xong là nén và hiện ảnh xem trước NGAY — promoter biết chắc máy đã nhận ảnh
  const handlePickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMsg(""); setPreparing(true);
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setPhoto(null);
    try {
      const blob = await compressPhoto(file);
      setPhoto({ blob, url: URL.createObjectURL(blob), sizeKB: Math.round(blob.size / 1024) });
    } catch {
      setPhoto({ blob: file, url: URL.createObjectURL(file), sizeKB: Math.round(file.size / 1024) });
    } finally { setPreparing(false); }
  };

  const clearPhoto = () => {
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Bọc timeout để upload không treo vô hạn khi mạng lỗi
  const withTimeout = (promise, ms, errMsg) => Promise.race([
    promise,
    new Promise((_, rej) => setTimeout(() => rej(new Error(errMsg)), ms)),
  ]);

  const handleAction = async (type) => {
    setLoading(true); setMsg("");
    try {
      const small = photo?.blob;
      if (!small) { setMsg("Vui lòng chụp/chọn ảnh trước"); setLoading(false); return; }
      setMsg(t("att.msg_upload"));
      const [photoUrl, gps] = await Promise.all([
        withTimeout(uploadAttendancePhoto(user.uid, type, small), 60000, "Tải ảnh quá lâu — kiểm tra kết nối mạng rồi thử lại"),
        getGPS(),
      ]);
      setMsg(t("att.msg_saving"));
      if (type === "checkin") {
        const holiday = await isHolidayDate(TODAY);
        await checkIn({ promoterId: user.uid, date: TODAY, photoUrl, lat: gps.lat, lng: gps.lng, isHoliday: !!holiday, holidayMultiplier: holiday?.multiplier || 1, scheduledHours, dayCredit: shift === "full" ? 1 : 0.5 });
        setMsg(holiday ? `Check-in thành công! Hôm nay là ngày lễ ×${holiday.multiplier} 🎉` : "Check-in thành công! ✓");
        addAuditLog({ type: "attendance_checkin", actorName: profile?.name || user.uid, actorRole: "promoter", promoterName: profile?.name, detail: `${profile?.name} check-in ngày ${TODAY}`, meta: { date: TODAY } });
      } else {
        await checkOut({ attendanceId: todayRecord.id, photoUrl, lat: gps.lat, lng: gps.lng });
        setMsg("Check-out thành công! ✓");
        addAuditLog({ type: "attendance_checkout", actorName: profile?.name || user.uid, actorRole: "promoter", promoterName: profile?.name, detail: `${profile?.name} check-out ngày ${TODAY}`, meta: { date: TODAY } });
      }
      getAttendanceByDate(user.uid, TODAY).then(r => {
        setTodayRecord(r);
        if (viewMonth === currentMonth) getAttendanceByMonth(user.uid, viewMonth).then(setHistoryRecords);
      });
      clearPhoto();
    } catch (err) {
      setMsg("Lỗi: " + err.message);
    } finally { setLoading(false); }
  };

  // Calendar helpers
  const daysInMonth = getDaysInMonth(new Date(viewMonth + "-01"));
  const firstDay = new Date(viewMonth + "-01").getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const recordMap = {};
  historyRecords.forEach(r => { recordMap[r.date] = r; });
  const appealMap = {};
  appeals.forEach(a => { appealMap[a.date] = a; });

  const isCurrentMonth = viewMonth === currentMonth;

  // Count approved appeals this month
  const approvedThisMonth = appeals.filter(a => a.date.startsWith(viewMonth) && a.status === "approved").length;
  const canAppealMore = approvedThisMonth < rules.maxAppealsPerMonth;

  const openAppeal = (dateStr) => {
    const rec = recordMap[dateStr];
    const appeal = appealMap[dateStr];
    const needsAppeal = !rec || !rec.checkoutTime || (rec.totalHours && rec.totalHours < 7);
    if (!needsAppeal && !appeal) return;
    setAppealModal({ date: dateStr, record: rec || null, appeal: appeal || null });
  };

  const holidaySet = new Set(holidayDates);

  const statusOf = (dateStr) => getAttendanceStatus({
    work: viewWork,
    rec: recordMap[dateStr],
    dateStr, todayStr: TODAY,
    isHoliday: holidaySet.has(dateStr),
    appealApproved: appealMap[dateStr]?.status === "approved",
  });

  const getDayClass = (dateStr) => {
    const rec = recordMap[dateStr];
    // Hôm nay chưa chấm công thì để trống, đừng vội tô là nghỉ tuần
    if (!rec && dateStr === TODAY) return "";
    const cls = ATT_STATUS[statusOf(dateStr)]?.cls || "";
    return isPendingLeave(rec) ? `${cls} att-s-pending` : cls;
  };

  return (
    <>
      {dayModal && (
        <DayRequestModal
          {...dayModal}
          standardHours={cfgFor(dayModal.date.slice(0, 7)).work.standardHours}
          isHolidayDay={holidaySet.has(dayModal.date)}
          promoterId={user.uid}
          promoterName={profile?.name || user.email}
          onClose={() => setDayModal(null)}
          onSubmitted={() => { getAttendanceByMonth(user.uid, viewMonth).then(setHistoryRecords); loadOt(); }}
        />
      )}

      {appealModal && (
        <AppealModal
          {...appealModal}
          promoterId={user.uid}
          promoterName={user.displayName || user.email}
          onClose={() => setAppealModal(null)}
          onSubmitted={() => { loadAppeals(); getAttendanceByMonth(user.uid, viewMonth).then(setHistoryRecords); }}
        />
      )}

      <div className="page-header">
        <h1>Check-in / Check-out</h1>
        <p>{format(new Date(), "EEEE, dd/MM/yyyy")}</p>
      </div>

      {/* Today section */}
      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div className="card">
          <div className="checkin-box">
            <div className="clock-display">{clock}</div>
            <div className="checkin-status-text">
              {!todayRecord ? "Chưa check-in hôm nay"
                : !todayRecord.checkoutTime ? `Đã check-in lúc ${todayRecord.checkinTime}`
                : `Hoàn tất ca làm việc (${todayRecord.totalHours}h)`}
            </div>
            {msg && (
              <div className={"alert " + (msg.startsWith("Lỗi") ? "alert-error" : "alert-success")} style={{ marginTop: 12 }}>
                {msg}
              </div>
            )}
            {!todayRecord && (
              <div style={{ margin: "14px 0 4px" }}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>{t("att.shift_label")}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" className={"btn btn-sm" + (shift === "full" ? " btn-primary" : "")}
                    style={{ flex: 1 }} onClick={() => setShift("full")}>{t("att.shift_full", { h: todayWork.standardHours })}</button>
                  <button type="button" className={"btn btn-sm" + (shift === "half" ? " btn-primary" : "")}
                    style={{ flex: 1 }} onClick={() => setShift("half")}>{t("att.shift_half", { h: todayWork.halfShiftHours })}</button>
                </div>
                {shift === "half" && (
                  <div style={{ fontSize: 11, color: "#C8152A", marginTop: 4 }}>{t("att.shift_half_note")}</div>
                )}
              </div>
            )}
            {photo ? (
              <div style={{ margin: "14px 0", border: "1.5px solid #16a34a", borderRadius: 10, padding: 10, background: "#F0FDF4" }}>
                <img src={photo.url} alt="Ảnh ca làm việc" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 8, display: "block" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <span style={{ flex: 1, fontSize: 12, color: "#15803d", fontWeight: 600 }}>
                    ✓ Đã nhận ảnh ({photo.sizeKB} KB) — bấm Check-in để gửi
                  </span>
                  <button type="button" className="btn btn-sm" onClick={() => fileRef.current?.click()}>Chụp lại</button>
                  <button type="button" className="btn btn-sm" onClick={clearPhoto}>Xóa</button>
                </div>
              </div>
            ) : (
              <div className="upload-zone" style={{ margin: "14px 0" }} onClick={() => !preparing && fileRef.current?.click()}>
                {preparing ? "⏳ Đang xử lý ảnh…" : <>📷 Chụp / chọn ảnh ca làm việc<br /><span style={{ fontSize: 11 }}>GPS sẽ được ghi nhận tự động</span></>}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handlePickPhoto} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => handleAction("checkin")}
                disabled={loading || preparing || !!todayRecord}>
                {loading ? "..." : "Check-in"}
              </button>
              <button className="btn" style={{ flex: 1 }} onClick={() => handleAction("checkout")}
                disabled={loading || preparing || !todayRecord || !!todayRecord?.checkoutTime}>
                Check-out
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Hôm nay</div>
          {todayRecord ? (
            <div className="salary-breakdown">
              <div className="salary-row"><span>Check-in</span><span>{todayRecord.checkinTime || "—"}</span></div>
              <div className="salary-row"><span>Check-out</span><span>{todayRecord.checkoutTime || "Chưa"}</span></div>
              <div className="salary-row"><span>Tổng giờ</span><span>{todayRecord.totalHours ? todayRecord.totalHours + "h" : "—"}</span></div>
              {todayRecord.isHoliday && (
                <div className="salary-row"><span>Ngày lễ</span><span className="badge badge-amber">×{todayRecord.holidayMultiplier}</span></div>
              )}
            </div>
          ) : (
            <div style={{ color: "#aaa", fontSize: 13 }}>Chưa có dữ liệu hôm nay</div>
          )}
          {"Notification" in window && Notification.permission !== "granted" && (
            <div className="alert alert-info" style={{ marginTop: 12, fontSize: 12 }}>
              💡 Bật thông báo để nhận nhắc nhở check-out lúc 22:00
              <br />
              <button className="btn btn-sm" style={{ marginTop: 6 }}
                onClick={() => Notification.requestPermission()}>
                Bật thông báo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* History section */}
      <div className="card">
        <div className="card-header" style={{ marginBottom: 12 }}>
          <div className="card-title">{t("sales.history")}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn btn-sm" onClick={() => setViewMonth(format(subMonths(new Date(viewMonth + "-01"), 1), "yyyy-MM"))}>←</button>
            <span style={{ fontSize: 13, fontWeight: 500, minWidth: 90, textAlign: "center" }}>
              Tháng {viewMonth.slice(5)}/{viewMonth.slice(0,4)}
            </span>
            <button className="btn btn-sm"
              onClick={() => setViewMonth(format(addMonths(new Date(viewMonth + "-01"), 1), "yyyy-MM"))}
              disabled={viewMonth >= currentMonth}>→</button>
          </div>
        </div>

        {isCurrentMonth && (
          <div className="alert alert-info" style={{ marginBottom: 12, fontSize: 12 }}>
            💡 Nhấn vào ngày <b>vắng</b> hoặc <b>thiếu giờ</b> để gửi giải trình lên admin.
            {!canAppealMore && <span style={{ color: "#A32D2D" }}> (Đã dùng {approvedThisMonth}/{rules.maxAppealsPerMonth} lần giải trình được duyệt tháng này)</span>}
          </div>
        )}

        <div className="att-grid" style={{ marginBottom: 16 }}>
          {["T2","T3","T4","T5","T6","T7","CN"].map(d => (
            <div key={d} className="att-day-header">{d}</div>
          ))}
          {Array(offset).fill(null).map((_, i) => <div key={"e"+i} className="att-day att-empty" />)}
          {days.map(d => {
            const dateStr = `${viewMonth}-${String(d).padStart(2, "0")}`;
            const rec = recordMap[dateStr];
            const appeal = appealMap[dateStr];
            const isToday = dateStr === TODAY;
            const isPast = dateStr < TODAY;
            const st = statusOf(dateStr);
            const needsAppeal = isPast && CAN_APPEAL.has(st);
            const ot = otMap[dateStr];
            const isLeaveRec = rec && (rec.dayType === DAY_TYPES.ANNUAL_LEAVE || rec.dayType === DAY_TYPES.UNPAID_LEAVE);
            const allowLeave = !rec && dateStr > TODAY;                         // ngày tương lai còn trống
            // Tăng ca chỉ có tác dụng ở tháng đã áp dụng cấu hình mới (giờ chuẩn + OT)
            const allowOt = dateStr >= TODAY && !ot && !isLeaveRec && !cfgFor(dateStr.slice(0, 7)).work.legacy;
            const canBook = allowLeave || allowOt || ot?.status === "pending";
            const clickable = needsAppeal || !!appeal || canBook;
            const cls = `att-day ${getDayClass(dateStr)} ${isToday ? "att-today" : ""}`;
            const onCellClick = () => {
              if (canBook) setDayModal({ date: dateStr, allowLeave, allowOt, ot });
              else if (needsAppeal || appeal) openAppeal(dateStr);
            };
            return (
              <div key={d} className={cls}
                onClick={clickable ? onCellClick : undefined}
                style={{ cursor: clickable ? "pointer" : "default", position: "relative" }}
                title={appeal ? `Giải trình: ${appeal.status}`
                  : isPendingLeave(rec) ? t("lv.pending_hint")
                  : canBook ? t("lv.book_hint")
                  : rec ? `${rec.checkinTime||""}–${rec.checkoutTime||""}` : t(`att.st_${st}`)}
              >
                {d}
                {appeal?.status === "pending" && <span style={{ position: "absolute", top: 1, right: 2, fontSize: 7, color: "#854F0B" }}>●</span>}
                {appeal?.status === "approved" && <span style={{ position: "absolute", top: 1, right: 2, fontSize: 7, color: "#3B6D11" }}>✓</span>}
                {ot && (
                  <span title={ot.status === "approved" ? t("ot.st_approved") : t("ot.st_pending")}
                    style={{ position: "absolute", bottom: 0, left: 0, right: 0, fontSize: 8, lineHeight: "10px",
                      fontWeight: 700, color: ot.status === "approved" ? "#15803d" : "#B45309",
                      textDecoration: ot.status === "approved" ? "none" : "underline dotted" }}>
                    {ot.kind === OT_KINDS.WEEKLY_OFF ? "NT " : ot.kind === OT_KINDS.HOLIDAY ? "L " : "+"}{ot.hours}h
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12, fontSize: 11, color: "#888" }}>
          {["full", "missing", "short", "holiday", "annualLeave", "unpaidLeave", "weeklyOff"].map(k => (
            <span key={k}>
              <span className={`att-day ${ATT_STATUS[k].cls}`}
                style={{ width: 14, height: 14, display: "inline-flex", borderRadius: 3, verticalAlign: "middle", marginRight: 4 }} />
              {t(`att.st_${k}`)}
            </span>
          ))}
          <span style={{ color: "#C8152A" }}>⌁ {t("lv.book_hint")}</span>
        </div>

        {/* Summary table */}
        {historyRecords.length > 0 && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <div className="metric-card" style={{ flex: "1 1 120px" }}>
                <div className="metric-label">{t("home.days_worked")}</div>
                <div className="metric-value">{historyRecords.filter(r => r.checkoutTime).length}</div>
              </div>
              <div className="metric-card" style={{ flex: "1 1 120px" }}>
                <div className="metric-label">{t("att.total_hours")}</div>
                <div className="metric-value">{historyRecords.reduce((a, r) => a + (r.totalHours || 0), 0).toFixed(1)}h</div>
              </div>
              <div className="metric-card" style={{ flex: "1 1 120px" }}>
                <div className="metric-label">{t("att.tab_appeals_short")}</div>
                <div className="metric-value">{approvedThisMonth} / {rules.maxAppealsPerMonth}</div>
                <div className="metric-sub">đã được duyệt</div>
              </div>
            </div>

            <table>
              <thead>
                <tr><th>Ngày</th><th>{t("att.checkin_time")}</th><th>{t("att.checkout_time")}</th><th>{t("att.hours")}</th><th>Trạng thái</th></tr>
              </thead>
              <tbody>
                {historyRecords.map(r => {
                  const appeal = appealMap[r.date];
                  return (
                    <tr key={r.id}>
                      <td>{r.date.slice(8)}/{r.date.slice(5,7)}</td>
                      <td>{r.checkinTime || "—"}</td>
                      <td>{r.checkoutTime || "—"}</td>
                      <td>{r.totalHours ? r.totalHours + "h" : "—"}</td>
                      <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {r.isHoliday && <span className="badge badge-amber">Lễ ×{r.holidayMultiplier}</span>}
                        {r.appealApproved && <span className="badge badge-blue">Giải trình OK</span>}
                        {r.totalHours && r.totalHours < 7 && !r.appealApproved && !appeal && <span className="badge badge-red">Thiếu giờ</span>}
                        {appeal?.status === "pending" && <span className="badge badge-amber">Đang xét duyệt</span>}
                        {appeal?.status === "rejected" && <span className="badge badge-red">Bị từ chối</span>}
                        {!r.checkoutTime && !appeal && !r.appealApproved && <span className="badge badge-red">Quên check-out</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {historyRecords.length === 0 && (
          <div style={{ color: "#aaa", fontSize: 13, textAlign: "center", padding: "16px 0" }}>
            Không có dữ liệu chấm công tháng {viewMonth.slice(5)}/{viewMonth.slice(0,4)}
          </div>
        )}
      </div>
    </>
  );
}
